import { useRequest } from 'ahooks';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, MessagePlugin } from 'tdesign-react';

import { LIMIT_PIC } from '@/const/form';
import { PIC_PRIVATE, PUBLIC, PUBLIC_NAME } from '@/const/library';
import { SEARCH_ID } from '@/const/search';
import {
  ILibrarySearchValue,
  LibraryObjectData,
  ResourceTabType,
  UserResourceType,
} from '@/interface/library';
import { ISpineResult } from '@/material/form-upload/folder';
import { BatchUploadType, getLimit } from '@/material/form-upload/service';
import libraryListReducer, { libraryReducerActions } from '@/reducer/libraryListReducer';
import objectReducer, { reducerActions } from '@/reducer/objectReducers';
import { CgiErrorCode } from '@/services/cgi';
import {
  getLastViewGroup,
  getLibraryGroupList,
  GetListResp,
  getResourceList,
  setLastViewGroup,
  UserResourceDetail,
} from '@/services/library';
import { useAppDispatch } from '@/store';
import { changeId } from '@/store/modules/library';
import { isPic, isUgc, isWoa } from '@/utils/getApiDomain';

import { ILibraryDialog } from './dialog';
import styles from './index.module.less';
import LibraryList from './list';
import HeaderPanel from './panel/header';
import LeftPanel from './panel/leftPanel';
import SearchPanel from './panel/rightPanel';
import { UploadFileProps } from './panel/rightPanel/library';

const defaultSize = 30;

export interface IReqParams {
  type: UserResourceType;
  resourceName: string;
  materialId: string;
  materialName: string;
  tabType: ResourceTabType;
  page: number;
  size: number;
}

export const defaultSearch: IReqParams = {
  type: UserResourceType.RESOURCE_TYPE_IMAGE, // 全部/图片/视频/音频
  resourceName: '',
  materialId: '', // 官方素材
  materialName: PUBLIC_NAME,
  tabType: ResourceTabType.TAB_TYPE_NOVA, // 默认 public
  page: 1,
  size: defaultSize,
};

const Library: React.FC<
Partial<ILibraryDialog> & {
  close: () => void;
  open: () => void;
  visible: boolean;
  type?: UserResourceType;
}
> = (props) => {
  const dispatchLibraryStore = useAppDispatch();

  const { close, confirmCallback, visible, type } = props;
  const searchType = type ? type : UserResourceType.RESOURCE_TYPE_IMAGE; // 如果传入有值这个参数不可变更

  const [searchParams] = useSearchParams();
  const storyId = searchParams.get(SEARCH_ID);

  const [leftLoading, setLeftLoading] = useState<boolean>(false);

  const [objectListLeftData, dispatchLeftList] = useReducer(libraryListReducer, { items: [] });
  const leftList = useMemo(() => objectListLeftData?.items, [objectListLeftData?.changeNumber]);

  const [hasNext, setHasNext] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(defaultSize);

  const [checkValue, setCheckValue] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const { runAsync: getList } = useRequest(getResourceList, { manual: true });

  const [objectData, dispatch] = useReducer(objectReducer, {
    params: {
      ...defaultSearch,
    },
    changeNumber: 0,
  });

  const [objectListData, dispatchList] = useReducer(libraryListReducer, { items: [] });
  const list = useMemo(() => objectListData?.items, [objectListData?.changeNumber]);

  const searchValue: any = useMemo(() => objectData?.params, [objectData?.changeNumber]);

  const clear = () => {
    setCheckValue([]);
    setPage(1);
    setSize(defaultSize);
    setLoading(false);
    setLeftLoading(false);
    setFailed(false);
    setHasNext(false);

    dispatchList({
      type: libraryReducerActions.INIT_EMPTY,
    });

    dispatchLeftList({
      type: libraryReducerActions.INIT_EMPTY,
    });

    dispatch({
      type: reducerActions.UPDATE_ITEM,
      payload: { ...defaultSearch },
    });
  };

  useEffect(() => {
    if (!visible) {
      clear();
    }
    return () => {
      clear();
    };
  }, [visible]);


  useEffect(() => () => {
    dispatchLibraryStore(changeId({ id: '' }));
  }, []);

  useEffect(() => {
    if (isWoa) {
      getGroupList({
        materialId: PUBLIC,
        tabType: ResourceTabType.TAB_TYPE_NOVA,
      });
      return;
    }
    if (isPic) {
      getGroupList({
        materialId: PIC_PRIVATE,
        tabType: ResourceTabType.TAB_TYPE_PIC,
      });
      return;
    }
    getLastGroup();
  }, [storyId, searchType, isWoa, isPic]);

  const getLastGroup = useCallback(async () => {
    // 先获取标记分组，再获取左侧列表，再获取右侧列表
    const { code, message, data } = await getLastViewGroup({ storyId: storyId || PUBLIC });
    if (code === CgiErrorCode.NO_ERROR) {
      const lastViewGroup = data?.lastViewGroup;
      getGroupList(lastViewGroup);
    } else {
      MessagePlugin.error(message || '获取上次记录分组失败');
    }
  }, [storyId, searchType, storyId]);

  const getGroupList = (lastViewGroup?: {
    id?: string;
    tabType: ResourceTabType;
    materialId?: string;
  }) => {
    const reqParams = getDefaultParams();
    getLeftList(reqParams, {
      isFirstUpdate: true,
      lastViewGroup,
    });
  };

  const getLeftList = async (params: IReqParams, extendsParams: {
    isFirstUpdate?: boolean;
    isChangeLimit?: boolean;
    lastViewGroup?: {
      id?: string;
      tabType: ResourceTabType;
      materialId?: string;
    }
  }) => {
    setLeftLoading(true);
    setLoading(true);
    const { isFirstUpdate, isChangeLimit, lastViewGroup } = extendsParams || {};

    const requestParams = lastViewGroup?.id && isFirstUpdate ? {
      materialId: lastViewGroup?.materialId || '',
      tabType: lastViewGroup?.tabType,
    } : {
      materialId: params?.materialId, // 如果有storyId 则默认是故事，其他为公开官方素材
      tabType: params?.tabType,
    };

    const { code, message, data } = await getLibraryGroupList({
      materialId: requestParams?.materialId,
      tabType: requestParams?.tabType,
    });

    if (code === CgiErrorCode.NO_ERROR) {
      const { groupList = [] } = data || {};

      const sortedGroupList = extendsParams?.lastViewGroup?.id
        ? [
          ...groupList.filter(item => item.id === lastViewGroup?.id),
          ...groupList.filter(item => item.id !== lastViewGroup?.id),
        ]
        : groupList;

      dispatchLeftList({
        type: libraryReducerActions.CHANGE_ALL,
        payload: {
          value: sortedGroupList,
        },
      });

      const defaultGroupOption = sortedGroupList?.[0];

      const update = lastViewGroup?.id && isFirstUpdate ? {
        materialId: lastViewGroup?.id,
        tabType: lastViewGroup?.tabType,
      } : {
        materialId: defaultGroupOption?.id || params.materialId, // 如果有storyId 则默认是故事，其他为公开官方素材
        tabType: params?.tabType,
      };

      const newParams = changeParams({ ...params, ...update });
      dispatch({
        type: reducerActions.UPDATE_ITEM,
        payload: { ...newParams },
      });

      if (isChangeLimit) {
        const limit = getLimit(params?.type);
        setLimit(limit);
      }

      await getServerList({ ...newParams }, {
        page: 1,
        size: defaultSize,
      });
    } else {
      dispatch({
        type: reducerActions.UPDATE_ITEM,
        payload: changeParams(params),
      });
      MessagePlugin.error(message || '分组拉取失败，请稍后重试');
    }
    setLeftLoading(false);
    setLoading(false);
  };

  const changeParams = (params: IReqParams) => {
    // 针对官方素材特殊处理，官方素材只有BGM
    const newType = type ? type : params?.tabType === ResourceTabType.TAB_TYPE_NOVA
      ? UserResourceType.RESOURCE_TYPE_AUDIO : params?.type;
    return { ...params, type: newType };
  };

  const updateResource = (_value: UserResourceDetail) => { };

  const getDefaultParams = useCallback(() => {
    let reqParams = { ...defaultSearch, tabType: ResourceTabType.TAB_TYPE_NOVA };

    // 绘本
    if (isPic) {
      reqParams = {
        ...reqParams,
        materialId: PIC_PRIVATE,
        tabType: ResourceTabType.TAB_TYPE_PIC,
        type: searchType,
      };
    } else if (storyId || searchType) {
      // 如果有storyId 则默认是故事，其他为公开官方素材
      const id = storyId || PUBLIC;
      const searchParams = {
        ...reqParams,
        materialId: id,
        type: searchType,
        tabType: id === PUBLIC ? ResourceTabType.TAB_TYPE_NOVA : ResourceTabType.TAB_TYPE_DEFAULT,
      };
      reqParams = { ...searchParams };
    } else {
      reqParams = { ...reqParams, materialId: PUBLIC, tabType: ResourceTabType.TAB_TYPE_NOVA };
    }

    return reqParams;
  }, [storyId, searchType, isPic]);

  const getNextPage = async () => {
    if (!hasNext) {
      return;
    }
    await getServerList(searchValue, {
      page: page + 1,
      next: true,
    });
  };

  const getServerList = async (searchValue: LibraryObjectData, params?: {
    page: number,
    size?: number,
    next?: boolean
  }) => {
    if (!params?.next) {
      setLoading(true);
    }

    setPage(params?.page || page || 1);
    setCheckValue([]);

    const { userResourceList = [], pageInfo, code, message } = await getList({
      size: params?.size || size || defaultSize,
      page: params?.page || page || 1,
      materialId: searchValue?.materialId,
      tabType: searchValue?.tabType,
      type: searchValue?.type,
      resourceName: searchValue?.resourceName,
    });
    if (code !== CgiErrorCode.NO_ERROR) {
      setFailed(true);
      MessagePlugin.error(message || '获取素材失败，请重试');
      return code;
    }
    setFailed(false);
    let newList = userResourceList;
    if (params?.next) {
      newList = list.concat(userResourceList);
    }
    const has_next = newList?.length < pageInfo?.total;

    dispatchList({
      type: libraryReducerActions.CHANGE_ALL,
      payload: {
        value: newList,
      },
    });

    setHasNext(has_next);
    setLoading(false);
    return code;
  };

  const handleUpload = (_value?: GetListResp['userResourceList']) => {
    getServerList({ ...searchValue }, { page: 1, size });
  };

  const [limit, setLimit] = useState({
    fileType: LIMIT_PIC.type,
    size: LIMIT_PIC.size,
    uploadType: LIMIT_PIC.uploadType,
    type: BatchUploadType.image,
  });

  const reNameCallback = (value: UserResourceDetail) => {
    dispatchList({
      type: libraryReducerActions.UPDATE_ITEM,
      payload: {
        value,
        keyIndex: value?.keyIndex as string,
      },
    });
  };

  const onChangeSearch = (params: {
    searchValue: ILibrarySearchValue;
    limit?: UploadFileProps['limit'];
  }) => {
    setCheckValue([]);
    setPage(1);
    setSize(defaultSize);

    const newParams = changeParams({ ...searchValue, ...params.searchValue });

    dispatch({
      type: reducerActions.UPDATE_ITEM,
      payload: newParams,
    });

    setLimit(getLimit(newParams?.type));
    getServerList(newParams, { page: 1, size: defaultSize });
  };

  const handleCheck = (value: string, reset?: boolean) => {
    if (reset) {
      setCheckValue([]);
      return;
    }
    if (type) {
      setCheckValue([value]);
    } else {
      if (checkValue.includes(value)) {
        setCheckValue(checkValue.filter(e => e !== value));
      } else {
        setCheckValue([...checkValue, value]);
      }
    }
  };

  const handleClose = () => {
    close();
  };

  const handleOK = () => {
    if (!checkValue.length) {
      return;
    }
    const id = checkValue?.[0];

    const choiceData = list?.find(e => e?.resourceId === id);

    setLastGroup();
    if (type === UserResourceType.RESOURCE_TYPE_SPINE) {
      confirmCallback?.({ ...choiceData?.spine as ISpineResult, directoryName: choiceData?.resourceName });
      return;
    }

    confirmCallback?.({
      url: choiceData?.url,
      name: choiceData?.resourceName || '未命名',
    });
  };

  const setLastGroup = async () => {
    if (searchValue?.tabType === ResourceTabType.TAB_TYPE_NOVA) {
      return;
    } // 不标记官方素材分组记录
    const { code, message } = await setLastViewGroup({
      lastViewGroup: {
        id: searchValue?.materialId,
        tabType: searchValue?.tabType,
        materialId: searchValue?.tabType === ResourceTabType.TAB_TYPE_NOVA ? PUBLIC : storyId || '',
      },
      storyId: storyId || '',
    });
    if (code !== CgiErrorCode.NO_ERROR) {
      MessagePlugin.success(message);
    }
  };

  const handleChangeMaterial = (material: {
    value: string
    label: string
  }) => {
    let extendsParams = {};
    if (material?.value === PUBLIC) {
      extendsParams = {
        tabType: ResourceTabType.TAB_TYPE_NOVA,
        materialId: material.value,
        type: type || UserResourceType.RESOURCE_TYPE_AUDIO,
      };
    } else if (material?.value === PIC_PRIVATE) {
      extendsParams = {
        tabType: ResourceTabType.TAB_TYPE_PIC,
        materialId: material.value,
        type: type || UserResourceType.RESOURCE_TYPE_IMAGE,
      };
    } else {
      extendsParams = {
        tabType: ResourceTabType?.TAB_TYPE_DEFAULT,
        materialId: material.value,
        type: type || UserResourceType.RESOURCE_TYPE_IMAGE,
      };
    }
    const reqParams = getDefaultParams();

    getLeftList({ ...reqParams, ...extendsParams }, {
      isChangeLimit: true,
    });
  };

  const deleteResource = (_ids: string[]) => { };

  const btnStyle = {
    width: 88,
    height: 32,
  };

  const disabled = useMemo(
    () => (isUgc
      && searchValue?.tabType === ResourceTabType.TAB_TYPE_NOVA),
    [searchValue?.tabType],
  );

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <HeaderPanel
          onChange={handleChangeMaterial}
          value={searchValue}
          storyId={storyId}
        />
        <div className={styles.material}>
          <div className={styles.left}>
            <LeftPanel
              storyId={storyId}
              searchValue={searchValue}
              list={leftList}
              loading={leftLoading}
              dispatchLeftList={dispatchLeftList}
              onChangeSearch={onChangeSearch}
              disabled={disabled}
            />
          </div>
          <div className={styles.content} id="libraryContentContendClient">
            <SearchPanel
              leftList={leftList}
              deleteResource={deleteResource}
              searchValue={searchValue}
              limit={limit}
              refreshList={data => getServerList(data, { page: 1, size: defaultSize })}
              onChangeSearch={onChangeSearch}
              handleUpload={handleUpload}
              type={type}
              checkValue={checkValue}
              onCheckedChange={handleCheck}
              list={list}
            />
            <LibraryList
              list={list}
              onCheckedChange={handleCheck}
              checkValue={checkValue}
              loading={loading || leftLoading}
              searchValue={searchValue}
              hasNext={hasNext}
              type={type}
              getNextPage={getNextPage}
              updateResource={updateResource}
              failed={failed}
              disabled={disabled}
              reNameCallback={reNameCallback}
            />
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        {type ? (
          <Button
            className="nova-info-button"
            size="medium"
            block
            theme="default"
            onClick={handleClose}
            style={{ ...btnStyle, marginRight: 16 }}
          >
            取消
          </Button>
        ) : null}
        <Button
          className={classNames(styles.confirm, {
            [styles.disabled]:
              type && !(checkValue.length && checkValue?.[0]),
          })}
          size="medium"
          block
          theme="primary"
          onClick={type ? handleOK : handleClose}
          style={btnStyle}
        >
          {type ? '确定' : '关闭'}
        </Button>
      </div>
    </div>
  );
};

export default Library;
